import React, { useEffect, useState } from 'react';
import { IconButton, PrimaryButton } from '../ui';
import ZoneManagement from './ZoneManagement';

const AssignEmployeesToZones = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialSelectedEmployees = [],
  employeeType = 'guards',
  siteId = null,
  initialZoneGuards = {},
  shiftValidationData = null
}) => {
  // Zone management state
  const [zoneGuards, setZoneGuards] = useState(initialZoneGuards);

  // Initialize zone guards when component opens
  useEffect(() => {
    if (isOpen) {
      console.log('AssignEmployeesToZones opened with:', {
        siteId,
        initialZoneGuards,
        employeeType
      });
      setZoneGuards(initialZoneGuards);
    }
  }, [isOpen, initialZoneGuards, siteId, employeeType]);

  // Zone management handlers
  const handleZoneSelect = (zoneId) => {
    // This can be used for any zone selection logic if needed
    console.log('Zone selected:', zoneId);
  };

  const handleAddEmployeeToZone = (zoneId) => {
    // This is now handled by the ZoneEmployeeAssignment component
    console.log('Add employee to zone:', zoneId);
  };

  const handleEmployeeAdd = (zoneId, employee) => {
    const currentZoneGuards = zoneGuards[zoneId] || [];
    const isAlreadyAssigned = currentZoneGuards.find(emp => emp.user_id === employee.user_id);
    
    // Check if employee is already assigned to any other zone
    const isAssignedToOtherZone = Object.keys(zoneGuards).some(otherZoneId => {
      if (otherZoneId !== zoneId && zoneGuards[otherZoneId]) {
        return zoneGuards[otherZoneId].find(emp => emp.user_id === employee.user_id);
      }
      return false;
    });
    
    if (isAssignedToOtherZone) {
      // Find the zone name for better error message
      const otherZoneId = Object.keys(zoneGuards).find(otherZoneId => {
        if (otherZoneId !== zoneId && zoneGuards[otherZoneId]) {
          return zoneGuards[otherZoneId].find(emp => emp.user_id === employee.user_id);
        }
        return false;
      });
      
      // This would need zone names to be passed down, for now use generic message
      console.warn(`Employee ${employee.display_name} is already assigned to another zone`);
      alert(`This employee is already assigned to another zone and cannot be assigned to multiple zones.`);
      return;
    }
    
    if (!isAlreadyAssigned) {
      const updatedZoneGuards = {
        ...zoneGuards,
        [zoneId]: [...currentZoneGuards, employee]
      };
      setZoneGuards(updatedZoneGuards);
    }
  };

  const handleEmployeeRemove = (zoneId, employee) => {
    const currentZoneGuards = zoneGuards[zoneId] || [];
    const updatedZoneGuards = {
      ...zoneGuards,
      [zoneId]: currentZoneGuards.filter(emp => emp.user_id !== employee.user_id)
    };
    setZoneGuards(updatedZoneGuards);
  };

  const handleNoteEdit = (zoneId, employee, newNote) => {
    const currentZoneGuards = zoneGuards[zoneId] || [];
    const updatedZoneGuards = {
      ...zoneGuards,
      [zoneId]: currentZoneGuards.map(emp => 
        emp.user_id === employee.user_id 
          ? { ...emp, note: newNote }
          : emp
      )
    };
    setZoneGuards(updatedZoneGuards);
  };

  const handleCloseDialog = () => {
    onClose();
  };

  const handleSaveZoneAssignments = () => {
    onSave(zoneGuards);
    handleCloseDialog();
  };

  return (
    <>
      {/* Dialog Box */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="w-[800px] h-[700px] relative bg-neutral-50/50 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-neutral-300/20 backdrop-blur-[30px] overflow-hidden">
            <div className="w-full px-8 py-6 flex justify-between items-center border-b border-gray-200">
              <div className="flex flex-col">
                <h3 className="text-xl font-bold text-black">
                  Assign employees to Zones
                </h3>
                <span className="text-sm text-gray-600">
                  {Object.keys(zoneGuards).length} zone{Object.keys(zoneGuards).length !== 1 ? 's' : ''} available
                </span>
              </div>
              {/* Close Button */}
              <IconButton 
                onClick={handleCloseDialog}
                icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                }
              />
            </div>

            {/* Dialog Content */}
            <div className="p-8 h-full overflow-y-auto">
              <div className="space-y-6">
                {!siteId ? (
                  <div className="text-center text-gray-500 py-8">
                    <div className="mb-2">⚠️</div>
                    <div className="text-sm">No site selected. Please select a site first.</div>
                  </div>
                ) : (
                  <ZoneManagement
                    siteId={siteId}
                    onZoneSelect={handleZoneSelect}
                    selectedZoneId={null}
                    onAddEmployeeToZone={handleAddEmployeeToZone}
                    onEmployeeAdd={handleEmployeeAdd}
                    onEmployeeRemove={handleEmployeeRemove}
                    onNoteEdit={handleNoteEdit}
                    zoneGuards={zoneGuards}
                    employeeType={employeeType}
                    shiftValidationData={shiftValidationData}
                  />
                )}
              </div>

              {/* Dialog Actions */}
              <div className="absolute bottom-6 left-8 right-8 flex gap-3 justify-center">
                <PrimaryButton 
                  onClick={handleSaveZoneAssignments}
                  disabled={Object.keys(zoneGuards).length === 0}
                >
                  Save Zone Assignments
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssignEmployeesToZones;
